import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'
import { ERROR_CODE_TRANSLATION_KEY_MAP, getErrorTranslationKey } from 'src/helpers/getApiErrorTranslationKey'
import { Button } from '@components/Button/Button'
import Footer from '@components/Footer'
import Header from '@components/Header'
import Page from '@components/Page'
import { useChoiceState } from '@context/ChoiceContext'
import { useContactDetailsState } from '@context/ContactDetailsContext'
import { addVerifyInfo, setInitialSmsSent, useIsExpired, useVerifyPhoneDispatch } from '@context/VerifyPhoneContext'
import { useCreateBookingForNewPatient } from '@hooks/useCreateBookingForNewPatient'
import { usePrepareBooking } from '@hooks/usePrepareBooking'
import { verifyPhoneNumberForNewPatient } from 'src/services/api/verifyPhone'
import validateSelectedAppointment from 'src/utils/validateSelectedAppointment'
import Spinner from '@components/Spinner'
import { PhoneVerificationBody } from '@components/PhoneVerificationBody/PhoneVerificationBody'
import { NewPatientContent } from '@components/PhoneVerificationBody/NewPatientContent'

export const CODE_INPUT_LENGTH = 5
export const INITAL_CODE_VALUE = Array(CODE_INPUT_LENGTH).fill('')

export const PhoneVerificationScreen = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [code, setCode] = useState(INITAL_CODE_VALUE)
  const contactDetails = useContactDetailsState()
  const { phone } = contactDetails
  const prepareBooking = usePrepareBooking()
  const onBackClicked = () => {
    history.push(routes.contact)
  }
  const choices = useChoiceState()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const verifyPhoneDispatch = useVerifyPhoneDispatch()
  const isExpired = useIsExpired(phone)
  const onCodeChange = (value: string[]) => {
    setCode(value)
  }

  const { mutate: verifyNumber } = useMutation(verifyPhoneNumberForNewPatient, {
    onSuccess: (data) => {
      verifyPhoneDispatch(addVerifyInfo(phone, data.validity))
    },
    onError: (error) => {
      setErrorMessage(t(getErrorTranslationKey(error)))
    },
  })

  const clearCodeInput = () => {
    setCode(INITAL_CODE_VALUE)
  }

  const { isLoading: isBookingInProgress, mutate: createBooking } = useCreateBookingForNewPatient((errorCode) => {
    setErrorMessage(t(errorCode))
    if (isExpired) {
      setErrorMessage(t(ERROR_CODE_TRANSLATION_KEY_MAP['PHONE_VERIFICATION_NOT_INITIATED']))
    }
    clearCodeInput()
  })
  const bookButtonDisabled = isBookingInProgress || code.some((char) => !char)

  const sendSmsCode = () => {
    verifyPhoneDispatch(setInitialSmsSent(phone))
    const booking = prepareBooking(contactDetails)
    verifyNumber(booking)
    clearCodeInput()
    setErrorMessage(undefined)
  }

  const onBookingClick = useCallback(() => {
    const isValidAppointment = validateSelectedAppointment(choices.selectedBookableDateAndTime)

    if (!isValidAppointment) {
      history.push(routes.appointment)
      return
    }

    const booking = prepareBooking(contactDetails, code.join(''))

    createBooking(booking)
  }, [choices, createBooking, history, prepareBooking, code, contactDetails])

  return (
    <Page
      header={<Header onBackClick={onBackClicked} />}
      body={
        <>
          {isBookingInProgress && <Spinner showOverlay size="large" />}
          <PhoneVerificationBody
            onCodeChange={onCodeChange}
            sendSmsCode={sendSmsCode}
            handleSubmit={onBookingClick}
            errorMessage={errorMessage}
            code={code}
            phone={phone}
            content={<NewPatientContent phone={phone} />}
          />
        </>
      }
      footer={
        <Footer
          button={
            <Button
              disabled={bookButtonDisabled}
              fullSize
              text={t('verify_phone_page_cta')}
              phoneSize="xl"
              onClick={onBookingClick}
            />
          }
        />
      }
    />
  )
}
